/**
 * Converts text to a valid slug format
 *
 * Transforms text into a URL-friendly slug that matches the slugSchema validation:
 * - Only lowercase letters, numbers, and single hyphens
 * - No consecutive hyphens
 * - No leading/trailing hyphens
 * - German umlauts normalized (ä→ae, ö→oe, ü→ue, ß→ss)
 *
 * @param text - The text to convert to a slug
 * @returns A valid slug string matching the pattern /^[a-z0-9]+(?:-[a-z0-9]+)*$/
 */
export function textToSlug(text: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }

  return (
    text
      // Convert to lowercase first to handle both upper and lowercase umlauts
      .toLowerCase()
      // Replace German umlauts and special characters BEFORE normalization
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      // Normalize to decompose other diacritics
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
      // Replace spaces and underscores with hyphens
      .replace(/[\s_]+/g, "-")
      // Remove all characters except lowercase letters, numbers, and hyphens
      .replace(/[^a-z0-9-]/g, "")
      // Replace multiple consecutive hyphens with a single hyphen
      .replace(/-+/g, "-")
      // Remove leading and trailing hyphens
      .replace(/^-+|-+$/g, "")
  );
}

/**
 * Converts a URL slug segment back to a readable label
 *
 * Reverses the slug transformation by:
 * - Decoding URI-encoded characters
 * - Converting German umlaut replacements back (ae→ä, oe→ö, ue→ü)
 * - Note: ss is NOT converted to ß, as it's ambiguous (could be original "ss" or converted "ß")
 * - Replacing hyphens with spaces
 * - Capitalizing the first letter of each word
 *
 * @param slug - The slug segment to convert (e.g., "oeffnungszeiten")
 * @returns A readable label (e.g., "Öffnungszeiten")
 */
export function slugToLabel(slug: string): string {
  if (!slug || typeof slug !== "string") {
    return "";
  }

  try {
    // Decode URI-encoded characters first
    const decoded = decodeURIComponent(slug);

    // Split by hyphens to handle multi-word slugs
    const words = decoded.split("-");

    return words
      .map((word) => {
        // Convert German umlaut replacements back to actual umlauts
        // Note: ss is NOT converted to ß to avoid false positives
        const withUmlauts = word
          .replace(/ae/gi, "ä")
          .replace(/oe/gi, "ö")
          .replace(/ue/gi, "ü");

        // Capitalize only the first letter of the word
        if (withUmlauts.length === 0) return withUmlauts;
        return (
          withUmlauts[0].toUpperCase() + withUmlauts.slice(1).toLowerCase()
        );
      })
      .join(" ");
  } catch {
    // If decodeURIComponent fails, fall back to original slug
    const words = slug.split("-");

    return words
      .map((word) => {
        // Note: ss is NOT converted to ß to avoid false positives
        const withUmlauts = word
          .replace(/ae/gi, "ä")
          .replace(/oe/gi, "ö")
          .replace(/ue/gi, "ü");

        if (withUmlauts.length === 0) return withUmlauts;
        return (
          withUmlauts[0].toUpperCase() + withUmlauts.slice(1).toLowerCase()
        );
      })
      .join(" ");
  }
}
